import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PersistentStorageManager } from '../../../../models/Storage';
import { useUpdateTemplateMutation } from '../../../../queries/Templates/useUpdateTemplateMutation';
import { Checkbox } from '../../../components/Checkbox';
import { styleSheet } from './styles';

const AutoSaveStorageKey = 'emailAutoSaveEnabled';

function formatTimestamp(date: Date): string {
	return (
		'Saved at ' +
		date.toLocaleTimeString(undefined, {
			hour: 'numeric',
			minute: '2-digit',
		})
	);
}

/** Serializes editor state to a stable string for change detection */
function serializeContent(editorState: Api.IRichContentEditorState | undefined): string | null {
	if (!editorState) {
		return null;
	}
	try {
		return JSON.stringify(editorState.getRawRichTextContent());
	} catch {
		return null;
	}
}

interface IEmailAutoSaveProps {
	/** Template associated with the email body — must have an id for auto-save to function */
	emailBodyTemplate: Api.ITemplate | undefined;
	/** Current editor content state */
	emailBodyEditorState: Api.IRichContentEditorState | undefined;
	/** Current subject text */
	subject: string | undefined;
	/** Master toggle — false when readonly, automation mode, custom email, or not saveable */
	enabled: boolean;
	/** Optional impersonation context for admin impersonation */
	impersonationContext?: Api.IImpersonationContext;
	/** Called when autosave is toggled on or off */
	onAutoSaveToggled?: (enabled: boolean) => void;
	/** Debounce delay in milliseconds (default: 2500) */
	debounceMs?: number;
	/** Optional CSS class for the wrapper */
	className?: string;
}

/**
 * Provides an "Autosave" checkbox with debounced auto-save for email templates.
 *
 * Saves via PATCH /template/{id} when the template has an id (user/account template).
 *
 * The parent controls visibility via `enabled` — autosave only appears when a direct
 * save is possible (same guard as the "Save" button). System/industry templates that
 * require "Save As" do not show the autosave checkbox.
 */
export function EmailAutoSave({
	emailBodyTemplate,
	emailBodyEditorState,
	subject,
	enabled,
	impersonationContext,
	onAutoSaveToggled,
	debounceMs = 2500,
	className,
}: IEmailAutoSaveProps) {
	const [autoSaveEnabled, setAutoSaveEnabled] = useState(
		() => PersistentStorageManager.local.get(AutoSaveStorageKey) === 'true'
	);
	const [isSaving, setIsSaving] = useState(false);
	const [savedLabel, setSavedLabel] = useState<string | null>(null);
	const lastSavedAtRef = useRef<Date | null>(null);

	const mountedRef = useRef(true);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

	// Baselines for change detection — initialized on first active render, updated after each save
	const lastSavedContentRef = useRef<string | null>(null);
	const lastSavedSubjectRef = useRef<string | null>(null);
	const isInitializedRef = useRef(false);

	const templateId = emailBodyTemplate?.id;
	const isActive = enabled && autoSaveEnabled && !!templateId;

	const templateMutation = useUpdateTemplateMutation();

	// Transition "Saved just now" → timestamp after 60s
	useEffect(() => {
		if (savedLabel !== 'Saved just now' || !lastSavedAtRef.current) {
			return;
		}
		const id = setTimeout(() => {
			if (mountedRef.current && lastSavedAtRef.current) {
				setSavedLabel(formatTimestamp(lastSavedAtRef.current));
			}
		}, 60_000);
		return () => clearTimeout(id);
	}, [savedLabel]);

	// Notify parent whether autosave is effectively active (both enabled and checked)
	const effectivelyActive = enabled && autoSaveEnabled;
	useEffect(() => {
		onAutoSaveToggled?.(effectivelyActive);
	}, [effectivelyActive, onAutoSaveToggled]);

	const currentContent = serializeContent(emailBodyEditorState);
	const currentSubject = subject ?? null;

	// Perform the actual save via template endpoint
	const performSave = useCallback(
		async (contentToSave: string | null, subjectToSave: string | null, attempt: number) => {
			if (!mountedRef.current) {
				return;
			}

			setIsSaving(true);

			try {
				const contentChanged = contentToSave !== lastSavedContentRef.current;
				const subjectChanged = subjectToSave !== lastSavedSubjectRef.current;

				if (!contentChanged && !subjectChanged) {
					setIsSaving(false);
					return;
				}

				if (!templateId || !emailBodyTemplate) {
					setIsSaving(false);
					return;
				}

				const updatedTemplate: Api.ITemplate = {
					...emailBodyTemplate,
					subject: subjectToSave ?? emailBodyTemplate.subject,
				};

				if (contentToSave !== null) {
					updatedTemplate.content = JSON.parse(contentToSave) as Api.IRawRichTextContentState;
				}

				await templateMutation.mutateAsync({ template: updatedTemplate, impersonationContext });

				if (!mountedRef.current) {
					return;
				}

				if (contentChanged) {
					lastSavedContentRef.current = contentToSave;
				}
				if (subjectChanged) {
					lastSavedSubjectRef.current = subjectToSave;
				}

				setIsSaving(false);
				lastSavedAtRef.current = new Date();
				setSavedLabel('Saved just now');
			} catch {
				if (!mountedRef.current) {
					return;
				}

				if (attempt < 3) {
					const delay = 1000 * Math.pow(2, attempt); // 1s, 2s, 4s

					if (retryTimerRef.current) {
						clearTimeout(retryTimerRef.current);
					}
					retryTimerRef.current = setTimeout(() => {
						if (mountedRef.current) {
							performSave(contentToSave, subjectToSave, attempt + 1);
						}
					}, delay);
				} else {
					setIsSaving(false);
					setSavedLabel('Save failed');
				}
			}
		},
		[templateId, emailBodyTemplate, templateMutation, impersonationContext]
	);

	// Stable ref so the debounce effect doesn't re-run when performSave identity changes
	const performSaveRef = useRef(performSave);
	performSaveRef.current = performSave;

	// Unified save effect: handles initialization, debouncing, and reset
	useEffect(() => {
		if (!isActive) {
			isInitializedRef.current = false;
			return;
		}

		// First activation: save immediately to establish baselines
		if (!isInitializedRef.current) {
			isInitializedRef.current = true;
			performSaveRef.current(currentContent, currentSubject, 0);
			return;
		}

		// Subsequent changes: debounce
		const contentChanged = currentContent !== lastSavedContentRef.current;
		const subjectChanged = currentSubject !== lastSavedSubjectRef.current;

		if (!contentChanged && !subjectChanged) {
			return;
		}

		debounceTimerRef.current = setTimeout(() => {
			if (mountedRef.current) {
				performSaveRef.current(currentContent, currentSubject, 0);
			}
		}, debounceMs);

		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [isActive, currentContent, currentSubject, debounceMs]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			mountedRef.current = false;
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current);
			}
		};
	}, []);

	if (!enabled) {
		return null;
	}

	const statusText = isSaving ? 'Saving...' : savedLabel;

	return (
		<div className={className}>
			{statusText ? <span className={css(styleSheet.autoSaveStatusText)}>{statusText}</span> : null}
			<Checkbox
				checked={autoSaveEnabled}
				id='enable-plaintext-autosave'
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					const checked = e.target.checked;
					setAutoSaveEnabled(checked);
					PersistentStorageManager.local.set(AutoSaveStorageKey, String(checked));
				}}
			>
				<div className={css(styleSheet.autoSaveCheckboxText)}>Autosave</div>
			</Checkbox>
		</div>
	);
}
